import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { usersApi } from '../api/users';
import { queryKeys } from '../lib/queryKeys';
import type { PaginationParams } from '../types/api';
import type { AdminUserUpdateRequest, AdminImpersonateRequest, AdminSendEmailRequest } from '../types/users';

export function useUsersList(params: PaginationParams & { is_active?: boolean; plan?: string }) {
  return useQuery({
    queryKey: queryKeys.users.list(params),
    queryFn: () => usersApi.list(params),
  });
}

export function useUserDetail(id: string) {
  return useQuery({
    queryKey: queryKeys.users.detail(id),
    queryFn: () => usersApi.detail(id),
    enabled: !!id,
  });
}

export function useUserActivity(id: string) {
  return useQuery({
    queryKey: queryKeys.users.activity(id),
    queryFn: () => usersApi.activity(id),
    enabled: !!id,
  });
}

export function useUpdateUser(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AdminUserUpdateRequest) => usersApi.update(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: queryKeys.users.detail(id) }); qc.invalidateQueries({ queryKey: queryKeys.users.all }); message.success('User updated'); },
  });
}

export function useDeactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usersApi.deactivate(id),
    onSuccess: (_, id) => { qc.invalidateQueries({ queryKey: queryKeys.users.detail(id) }); qc.invalidateQueries({ queryKey: queryKeys.users.all }); message.success('User deactivated'); },
  });
}

export function useReactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usersApi.reactivate(id),
    onSuccess: (_, id) => { qc.invalidateQueries({ queryKey: queryKeys.users.detail(id) }); qc.invalidateQueries({ queryKey: queryKeys.users.all }); message.success('User reactivated'); },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usersApi.deleteUser(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: queryKeys.users.all }); message.success('User deleted'); },
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (id: string) => usersApi.resetPassword(id),
    onSuccess: () => { message.success('Password reset email sent'); },
  });
}

export function useVerifyEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usersApi.verifyEmail(id),
    onSuccess: (_, id) => { qc.invalidateQueries({ queryKey: queryKeys.users.detail(id) }); message.success('Email verified'); },
  });
}

export function useImpersonateUser() {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AdminImpersonateRequest }) => usersApi.impersonate(id, data),
    onSuccess: () => { message.success('Impersonation session started'); },
  });
}

export function useSendEmail() {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AdminSendEmailRequest }) => usersApi.sendEmail(id, data),
    onSuccess: () => { message.success('Email sent'); },
  });
}
